/**
 * Feb 2026 R7 · Requirement brief detail — edit questions, refine with
 * AI, publish, share link, review responses.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Sparkles, Send, Link2, Copy, Trash2, Plus, ArrowUp, ArrowDown, MessageSquare, Save, FileText, Brain, Download, FolderPlus,
} from "lucide-react";

const TYPES = [
  ["short_text", "Short text"], ["long_text", "Long text"],
  ["single_choice", "Single choice"], ["multi_choice", "Multi-choice"],
  ["rating", "Rating 1–5"], ["yes_no", "Yes / No"],
  ["date", "Date"], ["number", "Number"], ["file", "File upload"],
];

export default function RequirementBriefDetail() {
  const { id } = useParams();
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(true);
  const [links, setLinks] = useState([]);
  const [responses, setResponses] = useState([]);
  const [tab, setTab] = useState("questions"); // questions | responses | share
  // AI refine
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [refineBusy, setRefineBusy] = useState(false);
  // Save as template
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  // Share link creation
  const [shareOpen, setShareOpen] = useState(false);
  const [shareDays, setShareDays] = useState(30);
  const [shareOtp, setShareOtp] = useState(false);
  // Groups (sections)
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupDraft, setGroupDraft] = useState({ title: "", description: "" });
  // Learn from responses
  const [learnOpen, setLearnOpen] = useState(false);
  const [learnBusy, setLearnBusy] = useState(false);
  const [learnPreview, setLearnPreview] = useState(null);
  const [learnInfo, setLearnInfo] = useState({ submitted_count: 0, can_learn: false });
  const [learnInstruction, setLearnInstruction] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId || !id) return;
    setBusy(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/requirement-briefs/${id}`),
        api.get(`/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/links`),
        api.get(`/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/responses`),
        api.get(`/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/learn/preview`).catch(() => ({ data: { submitted_count: 0, can_learn: false } })),
      ]);
      setQ(r1.data);
      setLinks(Array.isArray(r2.data) ? r2.data : []);
      setResponses(Array.isArray(r3.data) ? r3.data : []);
      setLearnInfo(r4.data || { submitted_count: 0, can_learn: false });
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  }, [activeWorkspaceId, id]);
  useEffect(() => { load(); }, [load]);

  const updateMeta = async (patch) => {
    try {
      const { data } = await api.patch(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}`, patch);
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const updateQuestion = async (qid, patch) => {
    try {
      const merged = { ...q.questions.find((x) => x.id === qid), ...patch };
      const { data } = await api.patch(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/questions/${qid}`,
        merged);
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const addQuestion = async () => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/questions`,
        { type: "short_text", text: "New question", required: false });
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  const deleteQuestion = async (qid) => {
    if (!confirm("Delete this question?")) return;
    try {
      await api.delete(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/questions/${qid}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const reorder = async (qid, dir) => {
    const ids = q.questions.map((x) => x.id);
    const i = ids.indexOf(qid);
    const j = i + (dir === "up" ? -1 : 1);
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/reorder`,
        { ids });
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Reorder failed")); }
  };

  const runRefine = async () => {
    if (!refineText.trim()) return;
    setRefineBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/refine`,
        { instruction: refineText });
      setQ(data);
      toast.success(`Refined to ${data.questions?.length || 0} questions`);
      setRefineOpen(false);
      setRefineText("");
    } catch (e) { toast.error(apiErrorMessage(e, "AI refine failed")); }
    finally { setRefineBusy(false); }
  };

  const publish = async () => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/publish`);
      setQ(data);
      toast.success("Published — now shareable");
      setShareOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
  };

  const createLink = async () => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/links`,
        { kind: "single", expires_in_days: Number(shareDays),
          require_otp: shareOtp });
      const url = `${window.location.origin}/rb/${data.link.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Link copied to clipboard");
      setShareOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Create link failed")); }
  };

  const revokeLink = async (link_id) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/requirement-briefs/links/${link_id}/revoke`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Revoke failed")); }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return;
    try {
      await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/save-as-template`,
        { name: tplName.trim(), tags: [] });
      toast.success("Saved to template library");
      setTplOpen(false);
      setTplName("");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const summarizeResponse = async (rid) => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/responses/${rid}/summarize`);
      toast.success("AI summary generated");
      setResponses((prev) => prev.map((r) => r.id === rid ? { ...r, ai_summary: data } : r));
    } catch (e) { toast.error(apiErrorMessage(e, "Summarize failed")); }
  };

  const generateFollowUp = async (rid) => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/follow-up`,
        { response_id: rid });
      toast.success("Follow-up questionnaire created");
      navigate(`/app/requirement-briefs/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Follow-up failed")); }
  };

  const addGroup = async () => {
    if (!groupDraft.title.trim()) return;
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/groups`,
        { title: groupDraft.title.trim(),
           description: groupDraft.description.trim() || null });
      setQ(data);
      setGroupOpen(false);
      setGroupDraft({ title: "", description: "" });
      toast.success("Section added");
    } catch (e) { toast.error(apiErrorMessage(e, "Add section failed")); }
  };

  const updateGroup = async (gid, patch) => {
    try {
      const { data } = await api.patch(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/groups/${gid}`,
        patch);
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const deleteGroup = async (gid) => {
    if (!confirm("Delete this section? Questions in it will move to Ungrouped.")) return;
    try {
      const { data } = await api.delete(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/groups/${gid}`);
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const reorderGroup = async (gid, dir) => {
    const ids = (q.groups || []).map((g) => g.id);
    const i = ids.indexOf(gid);
    const j = i + (dir === "up" ? -1 : 1);
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/groups/reorder`,
        { ids });
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Reorder failed")); }
  };

  const assignQuestionToGroup = async (qid, group_id) => {
    try {
      const { data } = await api.patch(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/questions/${qid}/group`,
        { group_id: group_id || null });
      setQ(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Move failed")); }
  };

  const openLearn = async () => {
    setLearnOpen(true);
    setLearnPreview(null);
    setLearnInstruction("");
  };

  const runLearnPreview = async () => {
    setLearnBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/learn`,
        { apply: false,
           instruction: learnInstruction.trim() || null });
      setLearnPreview(data);
      toast.success(`Analyzed ${data.responses_analyzed} response(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "AI learn failed")); }
    finally { setLearnBusy(false); }
  };

  const applyLearn = async () => {
    setLearnBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/${id}/learn`,
        { apply: true,
           instruction: learnInstruction.trim() || null });
      setQ(data);
      toast.success("Template refined from responses");
      setLearnOpen(false);
      setLearnPreview(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setLearnBusy(false); }
  };

  const downloadResponsePdf = async (rid, respName) => {
    try {
      const res = await api.get(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/responses/${rid}/pdf`,
        { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (q?.title || "brief").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40);
      a.download = `${safe}-${(respName || rid).replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 30)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "PDF download failed")); }
  };

  if (busy && !q) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!q) return <div className="p-8 text-sm text-muted-foreground">Not found</div>;

  return (
    <div className="min-h-screen" data-testid="requirement-brief-detail">
      <PageHeader
        title={q.title}
        description={q.description || "Requirement brief"}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" className="rounded-none h-9" onClick={() => setRefineOpen(true)} data-testid="rb-refine">
              <Sparkles className="w-4 h-4 mr-1" /> Refine with AI
            </Button>
            <Button
              variant="outline"
              className="rounded-none h-9"
              onClick={openLearn}
              disabled={!learnInfo.can_learn}
              title={learnInfo.can_learn
                ? `Learn from ${learnInfo.submitted_count} response(s)`
                : "Need at least 2 submitted responses to learn"}
              data-testid="rb-learn"
            >
              <Brain className="w-4 h-4 mr-1" /> Learn from responses
              {learnInfo.submitted_count > 0 && (
                <span className="ml-1 text-[10px] text-muted-foreground">({learnInfo.submitted_count})</span>
              )}
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => setTplOpen(true)} data-testid="rb-save-tpl">
              <FileText className="w-4 h-4 mr-1" /> Save as template
            </Button>
            {q.status !== "published" ? (
              <Button className="rounded-none h-9" onClick={publish} data-testid="rb-publish">
                <Send className="w-4 h-4 mr-1" /> Publish
              </Button>
            ) : (
              <Button className="rounded-none h-9" onClick={() => setShareOpen(true)} data-testid="rb-share">
                <Link2 className="w-4 h-4 mr-1" /> Share link
              </Button>
            )}
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        {/* Meta editor */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-border p-3">
          <div>
            <Label className="text-xs mb-1 block">Title</Label>
            <Input value={q.title} onChange={(e) => setQ({ ...q, title: e.target.value })}
                    onBlur={() => updateMeta({ title: q.title })}
                    className="h-9 rounded-none" data-testid="rb-title" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Description</Label>
            <Input value={q.description || ""} onChange={(e) => setQ({ ...q, description: e.target.value })}
                    onBlur={() => updateMeta({ description: q.description || "" })}
                    className="h-9 rounded-none" data-testid="rb-desc" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border">
          {["questions", "responses", "share"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === t ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
              data-testid={`rb-tab-${t}`}
            >
              {t} {t === "responses" ? `(${responses.length})` : ""} {t === "share" ? `(${links.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "questions" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {(q.groups?.length || 0)} section{(q.groups?.length || 0) === 1 ? "" : "s"} · {q.questions.length} question{q.questions.length === 1 ? "" : "s"}
              </div>
              <div className="flex-1" />
              <Button variant="outline" className="rounded-none h-8" onClick={() => setGroupOpen(true)} data-testid="rb-add-group">
                <FolderPlus className="w-4 h-4 mr-1" /> Add section
              </Button>
            </div>
            <GroupedQuestionsList
              groups={q.groups || []}
              questions={q.questions}
              onQuestionChange={updateQuestion}
              onQuestionDelete={deleteQuestion}
              onQuestionReorder={reorder}
              onGroupPatch={updateGroup}
              onGroupDelete={deleteGroup}
              onGroupReorder={reorderGroup}
              onAssign={assignQuestionToGroup}
            />
            <Button variant="outline" className="rounded-none w-full" onClick={addQuestion} data-testid="rb-add-question">
              <Plus className="w-4 h-4 mr-1" /> Add question
            </Button>
          </div>
        )}

        {tab === "responses" && (
          <div className="space-y-3">
            {responses.length === 0 && (
              <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">
                No responses yet. Share the link with your client.
              </div>
            )}
            {responses.map((r) => (
              <ResponseCard
                key={r.id} response={r} questions={q.questions} groups={q.groups || []}
                onSummarize={() => summarizeResponse(r.id)}
                onFollowUp={() => generateFollowUp(r.id)}
                onDownloadPdf={() => downloadResponsePdf(r.id, r.respondent_name || r.respondent_email)}
              />
            ))}
          </div>
        )}

        {tab === "share" && (
          <div className="space-y-3">
            {links.length === 0 && (
              <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">
                No share links yet. Publish and create a link.
              </div>
            )}
            {links.map((l) => (
              <div key={l.id} className="border border-border p-3 text-xs flex items-center gap-3" data-testid={`rb-link-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono truncate">{window.location.origin}/rb/{l.token}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Expires {new Date(l.expires_at).toLocaleDateString()} · views {l.view_count || 0}
                    {l.require_otp && " · OTP required"}
                    {l.revoked && " · REVOKED"}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-none h-7"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/rb/${l.token}`);
                            toast.success("Copied");
                          }}
                          data-testid={`rb-link-copy-${l.id}`}>
                  <Copy className="w-3 h-3" />
                </Button>
                {!l.revoked && (
                  <Button size="sm" variant="ghost" className="rounded-none h-7 text-rose-600"
                            onClick={() => revokeLink(l.id)}>Revoke</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refine dialog */}
      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="rb-refine-dialog">
          <DialogHeader>
            <DialogTitle>Refine questions with AI</DialogTitle>
            <DialogDescription>
              Free-form instruction like &ldquo;make questions more technical&rdquo; or &ldquo;add 3 budget-related questions&rdquo;. AI rewrites the current set.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={refineText} onChange={(e) => setRefineText(e.target.value)}
                        rows={4} className="rounded-none" data-testid="rb-refine-text" />
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setRefineOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={runRefine} disabled={refineBusy || !refineText.trim()} data-testid="rb-refine-run">
              {refineBusy ? "Refining…" : "Refine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save-as-template dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle>Save as template</DialogTitle></DialogHeader>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)}
                    placeholder="Template name" className="rounded-none" data-testid="rb-tpl-name" />
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setTplOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveAsTemplate} disabled={!tplName.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share link dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="rb-share-dialog">
          <DialogHeader><DialogTitle>Create a share link</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Expires in (days)</Label>
              <Input type="number" min={1} max={365} value={shareDays}
                        onChange={(e) => setShareDays(e.target.value)}
                        className="h-9 rounded-none" data-testid="rb-share-days" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <Switch checked={shareOtp} onCheckedChange={setShareOtp} data-testid="rb-share-otp" />
              Require email OTP before opening
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={createLink} data-testid="rb-share-create">Create + copy link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add section dialog */}
      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="rb-group-dialog">
          <DialogHeader>
            <DialogTitle>Add a section</DialogTitle>
            <DialogDescription>Group related questions together. Clients see each section as its own headed block.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Section title</Label>
              <Input value={groupDraft.title}
                        onChange={(e) => setGroupDraft({ ...groupDraft, title: e.target.value })}
                        className="h-9 rounded-none" data-testid="rb-group-title" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Description (optional)</Label>
              <Textarea value={groupDraft.description}
                             onChange={(e) => setGroupDraft({ ...groupDraft, description: e.target.value })}
                             rows={2} className="rounded-none" data-testid="rb-group-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setGroupOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={addGroup} disabled={!groupDraft.title.trim()} data-testid="rb-group-save">Add section</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Learn from responses dialog */}
      <Dialog open={learnOpen} onOpenChange={setLearnOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="rb-learn-dialog">
          <DialogHeader>
            <DialogTitle>Learn from past responses</DialogTitle>
            <DialogDescription>
              The AI reviews every submitted response ({learnInfo.submitted_count})
              and proposes a refined question set — sharper phrasing, new
              questions where clients volunteered info, removed redundancies.
            </DialogDescription>
          </DialogHeader>
          {!learnPreview ? (
            <div className="space-y-2">
              <Label className="text-xs mb-1 block">Extra instruction for the AI (optional)</Label>
              <Textarea value={learnInstruction}
                             onChange={(e) => setLearnInstruction(e.target.value)}
                             rows={3} className="rounded-none"
                             placeholder="e.g. Focus on tightening timeline questions"
                             data-testid="rb-learn-instruction" />
              <div className="text-[11px] text-muted-foreground">
                Preview first — nothing is applied until you confirm. The existing
                question set is snapshotted before any changes.
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setLearnOpen(false)}>Cancel</Button>
                <Button className="rounded-none" onClick={runLearnPreview}
                          disabled={learnBusy || !learnInfo.can_learn}
                          data-testid="rb-learn-preview">
                  {learnBusy ? "Analyzing…" : "Analyze responses"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="border-l-2 border-primary/40 bg-primary/5 p-2">
                <div className="text-[10px] label-eyebrow text-primary mb-1">AI learnings</div>
                {learnPreview.learnings?.length > 0 ? (
                  <ul className="list-disc pl-4 text-xs space-y-1">
                    {learnPreview.learnings.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground">No specific learnings surfaced.</div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  Analyzed {learnPreview.responses_analyzed} response(s)
                </div>
              </div>
              {(learnPreview.proposed_title && learnPreview.proposed_title !== q.title) && (
                <div className="text-xs">
                  <div className="text-muted-foreground">Proposed new title:</div>
                  <div className="font-semibold">{learnPreview.proposed_title}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] label-eyebrow text-muted-foreground mb-1">Proposed questions ({learnPreview.proposed_questions?.length || 0})</div>
                <div className="space-y-1 max-h-[300px] overflow-y-auto border border-border p-2">
                  {(learnPreview.proposed_questions || []).map((pq, i) => (
                    <div key={pq.id || i} className="text-xs py-1 border-b border-border last:border-b-0">
                      <span className="text-muted-foreground font-mono mr-1">#{i + 1}</span>
                      <span className="chip chip-info text-[9px] mr-1">{pq.type}</span>
                      {pq.required && <span className="chip chip-warning text-[9px] mr-1">req</span>}
                      <span>{pq.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setLearnPreview(null)}>Back</Button>
                <Button className="rounded-none" onClick={applyLearn}
                          disabled={learnBusy}
                          data-testid="rb-learn-apply">
                  {learnBusy ? "Applying…" : "Apply refined set"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuestionEditor({ question, index, total, onChange, onDelete, onReorder, groups, onAssign }) {
  const [q, setQ] = useState(question);
  useEffect(() => { setQ(question); }, [question]);
  const commit = (patch) => onChange(patch);
  const needsOptions = ["single_choice", "multi_choice"].includes(q.type);
  const hasGroups = Array.isArray(groups) && groups.length > 0;

  return (
    <div className="border border-border p-3 space-y-2" data-testid={`rb-q-${q.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1 pt-1">
          <button onClick={() => onReorder("up")} disabled={index === 0} className="text-muted-foreground disabled:opacity-30">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onReorder("down")} disabled={index === total - 1} className="text-muted-foreground disabled:opacity-30">
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-[10px] text-muted-foreground font-mono">#{index + 1}</span>
            <Select value={q.type} onValueChange={(v) => { setQ({ ...q, type: v }); commit({ ...q, type: v }); }}>
              <SelectTrigger className="h-8 w-40 rounded-none text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasGroups && (
              <Select
                value={q.group_id || "__none__"}
                onValueChange={(v) => {
                  const newGid = v === "__none__" ? null : v;
                  setQ({ ...q, group_id: newGid });
                  onAssign && onAssign(q.id, newGid);
                }}
              >
                <SelectTrigger className="h-8 w-44 rounded-none text-xs" data-testid={`rb-q-${q.id}-group`}>
                  <SelectValue placeholder="Section" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Ungrouped —</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <label className="inline-flex items-center gap-1 text-[11px]">
              <input type="checkbox" checked={q.required || false}
                       onChange={(e) => { setQ({ ...q, required: e.target.checked }); commit({ ...q, required: e.target.checked }); }} />
              Required
            </label>
            <label className="inline-flex items-center gap-1 text-[11px]">
              <input type="checkbox" checked={q.prefill_for_client || false}
                       onChange={(e) => { setQ({ ...q, prefill_for_client: e.target.checked }); commit({ ...q, prefill_for_client: e.target.checked }); }} />
              Prefill for client
            </label>
            <button onClick={onDelete} className="text-rose-600 ml-auto" data-testid={`rb-q-${q.id}-delete`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <Input value={q.text} onChange={(e) => setQ({ ...q, text: e.target.value })}
                    onBlur={() => commit({ ...q, text: q.text })}
                    className="h-9 rounded-none font-medium"
                    placeholder="Question text" data-testid={`rb-q-${q.id}-text`} />
          <Textarea value={q.description || ""} onChange={(e) => setQ({ ...q, description: e.target.value })}
                        onBlur={() => commit({ ...q, description: q.description || "" })}
                        rows={2} className="text-xs rounded-none"
                        placeholder="Help text (optional)" />
          {needsOptions && (
            <div>
              <Label className="text-[10px] text-muted-foreground">Options (one per line)</Label>
              <Textarea
                value={(q.options || []).map((o) => o.label || o.value).join("\n")}
                onChange={(e) => setQ({ ...q, options: e.target.value.split("\n").filter(Boolean).map((s) => ({ value: s.trim(), label: s.trim() })) })}
                onBlur={() => commit({ ...q })}
                rows={3} className="text-xs rounded-none font-mono"
              />
            </div>
          )}
          <div className="border-l-2 border-primary/40 pl-2 py-1 bg-primary/5">
            <div className="text-[9px] label-eyebrow text-primary">AI suggested answer (agency review only)</div>
            <Textarea value={q.suggested_answer || ""} onChange={(e) => setQ({ ...q, suggested_answer: e.target.value })}
                          onBlur={() => commit({ ...q, suggested_answer: q.suggested_answer || "" })}
                          rows={2} className="text-xs rounded-none"
                          data-testid={`rb-q-${q.id}-suggested`} />
            {q.prefill_for_client && (
              <div className="text-[10px] text-primary mt-1">✓ Will be pre-filled for the client to edit</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResponseCard({ response, questions, groups, onSummarize, onFollowUp, onDownloadPdf }) {
  const [open, setOpen] = useState(false);
  const grouped = (() => {
    const map = { __ungrouped__: [] };
    (groups || []).forEach((g) => { map[g.id] = []; });
    questions.forEach((qq) => {
      const key = qq.group_id && map[qq.group_id] ? qq.group_id : "__ungrouped__";
      map[key].push(qq);
    });
    return map;
  })();
  const renderQA = (qq) => {
    const val = response.answers?.[qq.id];
    return (
      <div key={qq.id} className="border-l-2 border-border pl-2 py-1">
        <div className="text-[10px] text-muted-foreground">{qq.text}</div>
        <div className="font-mono">
          {val === undefined || val === null || val === "" ? <span className="text-muted-foreground">—</span>
            : Array.isArray(val) ? val.join(", ")
            : (typeof val === "object" && val.filename) ? `[File] ${val.filename}`
            : String(val)}
        </div>
      </div>
    );
  };
  return (
    <div className="border border-border p-3" data-testid={`rb-resp-${response.id}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{response.respondent_name || response.respondent_email || "Anonymous"}</div>
          <div className="text-[10px] text-muted-foreground">
            {response.respondent_email} · submitted {response.submitted_at ? new Date(response.submitted_at).toLocaleString() : "in progress"}
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "View"}
        </Button>
      </div>
      {response.ai_summary && (
        <div className="mt-2 border border-primary/40 bg-primary/5 p-2 text-[11px]">
          <div className="font-semibold mb-1">AI summary</div>
          {response.ai_summary.summary && <p>{response.ai_summary.summary}</p>}
          {response.ai_summary.gaps?.length > 0 && (
            <div className="mt-1"><b>Gaps:</b> {response.ai_summary.gaps.join(" · ")}</div>
          )}
          {response.ai_summary.risks?.length > 0 && (
            <div className="mt-1"><b>Risks:</b> {response.ai_summary.risks.join(" · ")}</div>
          )}
          {response.ai_summary.next_actions?.length > 0 && (
            <ul className="list-disc pl-4 mt-1">
              {response.ai_summary.next_actions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
        </div>
      )}
      {open && (
        <div className="mt-3 space-y-3 text-xs">
          {(groups || []).map((g) => (
            (grouped[g.id] || []).length > 0 && (
              <div key={g.id}>
                <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">{g.title}</div>
                <div className="space-y-2">{(grouped[g.id] || []).map(renderQA)}</div>
              </div>
            )
          ))}
          {(grouped.__ungrouped__ || []).length > 0 && (
            <div>
              {(groups || []).length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Other</div>
              )}
              <div className="space-y-2">{grouped.__ungrouped__.map(renderQA)}</div>
            </div>
          )}
        </div>
      )}
      {response.status === "submitted" && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {!response.ai_summary && (
            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={onSummarize} data-testid={`rb-resp-${response.id}-summarize`}>
              <Sparkles className="w-3 h-3 mr-1" /> AI summary
            </Button>
          )}
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={onFollowUp} data-testid={`rb-resp-${response.id}-followup`}>
            <MessageSquare className="w-3 h-3 mr-1" /> Follow-up round
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7" onClick={onDownloadPdf} data-testid={`rb-resp-${response.id}-pdf`}>
            <Download className="w-3 h-3 mr-1" /> Client sign-off PDF
          </Button>
        </div>
      )}
    </div>
  );
}

function GroupedQuestionsList({ groups, questions, onQuestionChange, onQuestionDelete, onQuestionReorder, onGroupPatch, onGroupDelete, onGroupReorder, onAssign }) {
  // Bucket questions by their group_id (preserving question order within a bucket)
  const buckets = { __ungrouped__: [] };
  (groups || []).forEach((g) => { buckets[g.id] = []; });
  questions.forEach((qq) => {
    const key = qq.group_id && buckets[qq.group_id] ? qq.group_id : "__ungrouped__";
    buckets[key].push(qq);
  });
  const orderedGroups = groups || [];

  const renderQuestionRow = (qq) => {
    const totalIdx = questions.findIndex((x) => x.id === qq.id);
    return (
      <QuestionEditor
        key={qq.id}
        question={qq}
        index={totalIdx}
        total={questions.length}
        onChange={(patch) => onQuestionChange(qq.id, patch)}
        onDelete={() => onQuestionDelete(qq.id)}
        onReorder={(dir) => onQuestionReorder(qq.id, dir)}
        groups={orderedGroups}
        onAssign={onAssign}
      />
    );
  };

  return (
    <div className="space-y-6">
      {orderedGroups.map((g, gi) => (
        <div key={g.id} className="border-l-2 border-primary/60 pl-3 space-y-2" data-testid={`rb-group-${g.id}`}>
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-0.5">
              <button onClick={() => onGroupReorder(g.id, "up")} disabled={gi === 0} className="text-muted-foreground disabled:opacity-30">
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onGroupReorder(g.id, "down")} disabled={gi === orderedGroups.length - 1} className="text-muted-foreground disabled:opacity-30">
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <Input
              defaultValue={g.title}
              onBlur={(e) => { if (e.target.value.trim() !== g.title) onGroupPatch(g.id, { title: e.target.value.trim() }); }}
              className="h-8 rounded-none font-semibold text-sm max-w-md"
              data-testid={`rb-group-${g.id}-title`}
            />
            <span className="text-[10px] text-muted-foreground">({(buckets[g.id] || []).length})</span>
            <div className="flex-1" />
            <button onClick={() => onGroupDelete(g.id)} className="text-rose-600 text-[11px] flex items-center gap-1" data-testid={`rb-group-${g.id}-delete`}>
              <Trash2 className="w-3.5 h-3.5" /> Remove section
            </button>
          </div>
          <Input
            defaultValue={g.description || ""}
            onBlur={(e) => { if ((e.target.value || "") !== (g.description || "")) onGroupPatch(g.id, { description: e.target.value }); }}
            placeholder="Section description (optional, shown to client)"
            className="h-8 rounded-none text-xs max-w-2xl"
          />
          <div className="space-y-3">
            {(buckets[g.id] || []).length === 0 ? (
              <div className="text-[11px] text-muted-foreground border border-dashed border-border p-3">
                No questions in this section yet. Use the section dropdown on any question to move it here.
              </div>
            ) : (
              (buckets[g.id] || []).map(renderQuestionRow)
            )}
          </div>
        </div>
      ))}
      {(buckets.__ungrouped__ || []).length > 0 && (
        <div className="space-y-2">
          {orderedGroups.length > 0 && (
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Ungrouped</div>
          )}
          <div className="space-y-3">
            {buckets.__ungrouped__.map(renderQuestionRow)}
          </div>
        </div>
      )}
    </div>
  );
}
